import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import LoadingButton from '@app/src/components/LoadingButton'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { QuestionCategoryApi, QuestionCategoryType } from '@app/src/types/resourceExplorer'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Grid, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

export const CATEGORY_TYPE_OPTIONS: { value: QuestionCategoryType; label: string }[] = [
  { value: 'Basic', label: 'Basic' },
  { value: 'Regular', label: 'Regular' },
  { value: 'ESRS', label: 'ESRS' },
]

interface CategoryFormData {
  name: string
  categoryType: QuestionCategoryType
}

const AddCategory: React.FC = () => {
  const { register, handleSubmit, watch, setValue, control } = useForm<CategoryFormData>()
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const { mutateAsync: createCategoryAsync, isPending: isCreatingCategory } = useCreateResource<
    QuestionCategoryApi,
    CategoryFormData
  >()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()

  const categoryName = watch('name')
  const categoryType = watch('categoryType')

  const allFieldsFilled = categoryName?.length > 0 && Boolean(categoryType)

  const onSubmit = async (formData: CategoryFormData) => {
    await createCategoryAsync(
      { url: endpoints.questionCategory, body: formData },
      {
        onSuccess: () => {
          showSnackbar({ message: 'The category was created', severity: NotificationSeverity.success })
          setValue('name', '')
          setValue('categoryType', undefined)
          queryClient.invalidateQueries({
            predicate: query => query.queryKey.includes(FetchKey.QuestionCategories),
          })
        },
        onError: error => {
          if (error.statusCode === 400 || error.statusCode === 409)
            showSnackbar({ message: error.body?.message, severity: NotificationSeverity.error })
          else showErrorNotification({ requestError: error })
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="subtitle1">Add category</Typography>
      <Typography variant="caption">
        Once you add a category, you can immediately add tags to it.
        <br />
        Categories with the type <i>Basic</i> are filterable at the top of the Questions library and appear first in the
        filter drawer and question editor.
      </Typography>
      <Grid container spacing={1} pt={2}>
        <Grid item md={3} xs={12}>
          <TextField
            variant="outlined"
            sx={{ width: '100%', height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
            inputRef={register()}
            label="Category name"
            name="name"
            inputProps={{ maxLength: 60 }}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <Select
            control={control}
            variant="outlined"
            name="categoryType"
            fieldLabel="Category Type"
            options={CATEGORY_TYPE_OPTIONS}
            required
            rules={{ required: true }}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <LoadingButton
            variant="contained"
            type="submit"
            disabled={!allFieldsFilled}
            loading={isCreatingCategory}
            sx={{ height: '100%', width: '50%' }}
          >
            {formatMessage({ id: 'general.save' })}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  )
}

export default AddCategory
